import * as React from 'react';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { useMutation, useQuery } from '@apollo/client';
import { Clear, Input, InputErrMess, InputWrap, Label, Submit } from './styles';
import { FETCH_ALL_PAPERS, FETCH_STUDENT_DATA, REGISTER_STUDENT } from './graphql';
import {
  FetchStudentDataMutation,
  GetAllPapersQuery,
  RegisterStudentMutation,
} from '../../../../../generated/graphql';
import { BeatLoader, ClipLoader } from 'react-spinners';
import { DisplayPaper } from './components/DisplayPaper';
import { toast } from 'react-toastify';
import { useDidMountEffect } from '../../../../../helpers';
import { TabContentOverlayLoader } from '../../../../../components';

export interface Paper {
  __typename?: 'Paper' | undefined;
  courseName?: string | null | undefined;
  _id?: string | null | undefined;
  courseCode?: string | null | undefined;
  class?: string | null | undefined;
}

export const StudentForm: React.FC = (): JSX.Element => {
  const [selectedCourses, setSelectedCourses] = React.useState<Paper[]>([]);
  const [showSelectedCourseError, setShowSelectedCourseError] = React.useState(false);
  const [refecthCount, setRefetchCount] = React.useState(0);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [filteredPapers, setFilteredPapers] = React.useState<any>(null);
  const [searchValue, setSearchValue] = React.useState('');
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [myData, setMyData] = React.useState<any[]>([]);
  const { data, loading, error } = useQuery<GetAllPapersQuery>(FETCH_ALL_PAPERS);
  const [registerStudent] = useMutation<RegisterStudentMutation>(REGISTER_STUDENT);
  const [fetchStudentData, { loading: studentDataFetchLoading }] =
    useMutation<FetchStudentDataMutation>(FETCH_STUDENT_DATA, {
      onCompleted: (_data) => {
        if (_data.fetchStudentData) {
          toast.info('Found student with this index number!', {
            autoClose: 2000,
          });
          setFieldValue('name', _data.fetchStudentData.name);
          setMyData(_data.fetchStudentData.papersForStudentClass as never);
          setSelectedCourses(_data.fetchStudentData.registeredPapers as never);
        } else {
          toast.warn('No student with this index exist');
        }
      },
    });

  const validationSchema = Yup.object().shape({
    name: Yup.string().required("* Student's full name must be entered"),
    indexNumber: Yup.string()
      .required("* Student's index number must be entered")
      .length(7, "* Student's index number must be 7 digits"),
  });
  const initialValues = {
    name: '',
    indexNumber: '',
  };
  const {
    handleBlur,
    handleChange,
    handleSubmit,
    isSubmitting,
    handleReset,
    values,
    errors,
    setFieldValue,
  } = useFormik({
    validationSchema,
    initialValues,
    onSubmit: async (formValues) => {
      if (selectedCourses.length === 0) {
        setShowSelectedCourseError(true);
        return;
      }

      const papersWithIDs = selectedCourses.map((selected) => {
        return `${selected._id}`;
      });

      try {
        await registerStudent({
          variables: {
            studentDetails: {
              indexNumber: formValues.indexNumber,
              name: formValues.name,
              papers: papersWithIDs,
              student_class: selectedCourses[0].class && myData[0].class,
            },
          },
        });
        toast.success('Registered student successfully!');
      } catch (err) {
        if (err.message.includes('Failed to fetch')) {
          toast.error(
            `Registering student failed.
               Network error occured, please check your internet connection!`,
          );
        } else
          toast.error(
            err?.message || 'Error Occured in registering student. Student might already exist',
          );
      }
    },
  });

  // add effect to prepopulate the student details in the form
  const indexNumberInputDep = values.indexNumber.length === 7;
  useDidMountEffect(async () => {
    if (values.indexNumber.length === 7) {
      // fetch for the already existing students details with the index number
      try {
        await fetchStudentData({
          variables: { indexNumber: values.indexNumber },
        });
      } catch (err) {
        console.log(err);
        toast.error(err.message);
      }
    }
  }, [indexNumberInputDep]);

  React.useEffect(() => {
    if (data?.getPapers) setMyData(data.getPapers);
    if (error) toast.error(error.message);
  }, [data, refecthCount, error]);

  React.useEffect(() => {
    const searchResults = data?.getPapers?.filter((paper) => {
      if (
        paper?.courseCode?.toLowerCase().includes(searchValue.toLowerCase()) ||
        paper?.courseName?.toLowerCase().includes(searchValue.toLowerCase()) ||
        paper?.class?.toLowerCase().includes(searchValue.toLowerCase())
      ) {
        return paper;
      } else {
        return null;
      }
    });

    if (!searchResults) setFilteredPapers(null);

    setFilteredPapers(searchResults);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue]);

  const addToSelectedCourse = (paper: Paper): void => {
    if (selectedCourses.includes(paper)) {
      // remove from selected courses
      const filtered = selectedCourses.filter((selected) => paper._id !== selected._id);
      setSelectedCourses(filtered);
      return;
    }
    setSelectedCourses([...selectedCourses, paper]);
  };

  return (
    <form
      className={`mt-14 relative ${studentDataFetchLoading && 'pointer-events-none'}`}
      onSubmit={handleSubmit}
    >
      {studentDataFetchLoading && (
        <TabContentOverlayLoader loadingMess="Checking for existing student's data" />
      )}
      <div className="pr-10 2xl:pr-16 pb-10 grid grid-cols-2 gap-20">
        <InputWrap>
          <Label>Index Number</Label>
          <Input
            value={values.indexNumber}
            isGrird={true}
            onChange={handleChange}
            name="indexNumber"
            disabled={isSubmitting}
            onBlur={handleBlur}
            placeholder="Enter index number of Student"
          />
          {errors.indexNumber && <InputErrMess>{errors.indexNumber}</InputErrMess>}
        </InputWrap>
        <InputWrap>
          <Label>Student Name</Label>
          <Input
            value={values.name}
            isGrird={true}
            onChange={handleChange}
            disabled={isSubmitting}
            name="name"
            onBlur={handleBlur}
            placeholder="Enter full name of Student"
          />
          {errors.name && <InputErrMess>{errors.name}</InputErrMess>}
        </InputWrap>
      </div>
      <div className="flex p-3 bg-slate-50 -mt-4 rounded-md mb-8">
        <div className="flex w-1/2 gap-1 flex-col relative">
          <h4 className="text-center font-extrabold my-2">Select student&apos;s courses</h4>
          <div
            className="bg-gray-100 select-none w-11/12
             rounded-md p-5 gap-3 shadow flex flex-col"
          >
            <h3 className="font-extrabold">All Papers</h3>
            {loading ? (
              <div className="flex justify-center items-center">
                <ClipLoader size={40} />
              </div>
            ) : (
              <>
                <div className="w-full">
                  <input
                    value={searchValue}
                    onChange={(e) => setSearchValue(e.target.value)}
                    className="py-2 w-full px-3 border-gray-300 bg-white rounded-md
                      outline-none"
                    placeholder="Search for a paper by course code or name"
                  />
                </div>
                <div
                  className="flex flex-col gap-3 lg:h-40 2xl:h-56
                 2xl:pb-10 pb-9 pr-2 overflow-auto"
                >
                  {error || filteredPapers?.length === 0 || myData.length === 0 ? (
                    <p className="text-center text-red-600">{'Opps! ):'} No paper was found</p>
                  ) : (
                    <>
                      {!filteredPapers
                        ? myData?.map((paper) => (
                            <DisplayPaper
                              key={paper?._id}
                              paper={paper}
                              addToSelectedCourse={addToSelectedCourse}
                              reset={refecthCount}
                            />
                          ))
                        : // eslint-disable-next-line @typescript-eslint/no-explicit-any
                          filteredPapers?.map((paper: any) => (
                            <DisplayPaper
                              key={paper?._id}
                              paper={paper}
                              addToSelectedCourse={addToSelectedCourse}
                              reset={refecthCount}
                            />
                          ))}
                    </>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
        <div className="w-1/2">
          <h4 className="text-center font-extrabold my-2">List of registered courses</h4>
          {selectedCourses.length === 0 ? (
            <div className="text-center">
              <p>You have not select any course for student yet!</p>
              {showSelectedCourseError && (
                <p className="text-red-600">* Please you must select at least one course!</p>
              )}
            </div>
          ) : (
            <div className="flex gap-2 overflow-auto lg:h-72 2xl:h-96 pb-28">
              <div className="w-11/12 flex flex-col gap-2">
                {selectedCourses.map((course) => (
                  <h5 key={course._id} className="p-3 bg-blue-100 rounded-md ">
                    <b> {course.courseCode}, </b>
                    {course.courseName} - {course.class}
                  </h5>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
      <div className="mt-8">
        <Submit className="font-bold" type="submit">
          {isSubmitting ? <BeatLoader size={14} color="#fff" /> : 'Register'}
        </Submit>
        <Clear
          className="font-bold ml-10"
          onClick={(e) => {
            handleReset(e);
            setSelectedCourses([]);
            setRefetchCount(refecthCount + 1);
            setSearchValue('');
          }}
        >
          Reset
        </Clear>
      </div>
    </form>
  );
};
